import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {Object} PayableInvoice
 * @property {number} id - Invoice ID
 * @property {string} invoice_no - Invoice number (e.g., "YQINV-20260128-26")
 * @property {string} status - Invoice status (e.g., "partial", "pending", "paid")
 * @property {string} total_amount - Total invoice amount
 * @property {string} paid_amount - Already paid amount
 * @property {number} due_amount - Remaining amount to be paid
 * @property {string} currency - Currency code (e.g., "INR")
 * @property {string} issued_at - Invoice issue date
 * @property {number} cycle_id - Billing cycle ID
 * @property {boolean} can_pay - Whether online payment is allowed
 */

/**
 * @typedef {Object} GetInvoiceDetailResponse
 * @property {boolean} success - Whether the request was successful
 * @property {string} message - Response message
 * @property {PayableInvoice[]} data - Array of payable invoices
 */

/**
 * @description Custom hook to fetch invoice details for payment
 */
export const useGetInvoiceDetailForPayment = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  const { axiosInstance } = useAxios();

  /**
   * @description Fetch invoice details for payment
   * @async
   * @param {number} invoiceId - The invoice ID to fetch details for
   * @param {(data: PayableInvoice) => void} [onSuccess] - Callback function to be called on success
   * @returns {Promise<PayableInvoice | null>} Returns invoice data on success, null on failure
   */
  const getInvoiceDetail = async (invoiceId, onSuccess) => {
    try {
      setLoading(true);
      setError("");
      setSuccess(false);
      setInvoiceData(null);

      const response = await axiosInstance.get(
        `${TENANT_DOMAIN}/billing/invoices/payable`,
        {
          params: {
            invoice_id: invoiceId,
          },
        },
      );

      const responseData = response.data;

      // Extract the first invoice from the data array
      const invoice = responseData?.data?.[0] || null;

      if (invoice) {
        setInvoiceData(invoice);
        setSuccess(true);

        if (onSuccess) onSuccess(invoice);

        return invoice;
      } else {
        setError("Invoice not found");
        return null;
      }
    } catch (err) {
      setSuccess(false);
      setInvoiceData(null);

      if (err?.response?.status === 500 || err?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else if (err?.response?.status === 404) {
        setError("Invoice not found");
      } else {
        const apiErrors = err?.response?.data?.errors;
        const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];
        setError(
          firstError || err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN,
        );
      }

      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset the hook state
   */
  const reset = () => {
    setLoading(false);
    setError("");
    setSuccess(false);
    setInvoiceData(null);
  };

  return {
    loading,
    error,
    success,
    invoiceData,
    getInvoiceDetail,
    reset,
  };
};

export default useGetInvoiceDetailForPayment;

